import { type FormEvent, useEffect, useRef, useState } from "react";
import type { User } from "firebase/auth";
import * as api from "./api";
import { dueText, newestAfter, reasonProblem, RESET_REASON_MAX, resetProblem, weeksLeft } from "./forecast";
import { weekGroupLabel, weekGroupsFor } from "./plan";
import { type Saved, saveFailure } from "./save";

/** Start the forecast again from a day, or clear the reset, with a reason (rule A-5). */
export function ForecastReset({
  account,
  user,
  today,
  onSaved,
}: {
  account: api.AccountDetail;
  user: User;
  today: string;
  onSaved: (next: api.AccountDetail) => void;
}) {
  const plan = account.plan;
  const [open, setOpen] = useState<"reset" | "clear" | null>(null);
  const [start, setStart] = useState(today);
  const [reason, setReason] = useState("");
  const [weeks, setWeeks] = useState(plan?.week_group ?? "");
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<Saved | null>(null);
  const [wrong, setWrong] = useState<{ field: "start" | "reason"; text: string; at: number } | null>(null);
  const statusRef = useRef<HTMLParagraphElement>(null);
  const startRef = useRef<HTMLInputElement>(null);
  const reasonRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (wrong) (wrong.field === "start" ? startRef : reasonRef).current?.focus();
  }, [wrong?.at]);
  if (!plan || !account.can_reset_forecast) return null;

  const capWeeks = account.projection?.cap_weeks ?? null;
  const before = account.projection?.weeks_left ?? null;
  const after = weeksLeft(capWeeks, newestAfter(account.pickups, start, today), today);
  const long = reason.trim().length > RESET_REASON_MAX;

  const close = () => {
    setOpen(null);
    setReason("");
    setWrong(null);
  };
  const save = async (reset: api.ForecastReset, done: string) => {
    setSaving(true);
    setStatus(null);
    try {
      const token = await user.getIdToken();
      onSaved(await api.resetForecast(token, account.id, reset));
      setStatus({ ok: true, text: done });
      close();
    } catch (e) {
      setStatus(saveFailure(e, "The forecast reset"));
    } finally {
      setSaving(false);
      requestAnimationFrame(() => statusRef.current?.focus());
    }
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const problem = open === "clear" ? reasonProblem(reason) : resetProblem(start, reason, today);
    if (problem) {
      setStatus(null);
      setWrong({ field: problem === reasonProblem(reason) ? "reason" : "start", text: problem, at: Date.now() });
      return;
    }
    setWrong(null);
    if (open === "clear") void save({ start: null, reason: reason.trim() }, "The forecast uses all pickups again.");
    else void save({ start, reason: reason.trim(), week_group: weeks }, "The forecast starts again from the day you gave.");
  };

  return (
    <div className="forecast-reset" data-testid="forecast-reset">
      <p ref={statusRef} tabIndex={-1} role="status" data-testid="forecast-reset-status" className={status && !status.ok ? "error" : undefined}>
        {status?.text}
      </p>
      {!open && (
        <div className="actions">
          <button type="button" data-testid="forecast-reset-open" onClick={() => setOpen("reset")}>
            Reset the forecast…
          </button>
          {plan.forecast_from && (
            <button type="button" data-testid="forecast-clear-open" onClick={() => setOpen("clear")}>
              Clear the reset…
            </button>
          )}
        </div>
      )}
      {open && (
        <form className="lead-form" data-testid="forecast-reset-form" onSubmit={submit} noValidate>
          {open === "reset" ? (
            <>
              <p>The forecast uses only the pickups from this day on. The container counts as empty on this day.</p>
              <label htmlFor="forecast-start">Start day</label>
              <input
                id="forecast-start"
                type="date"
                data-testid="forecast-start"
                value={start}
                max={today}
                ref={startRef}
                aria-invalid={wrong?.field === "start"}
                aria-describedby={wrong?.field === "start" ? "forecast-field-error" : undefined}
                onChange={(event) => {
                  setStart(event.target.value);
                  setWrong(null);
                }}
              />
              {wrong?.field === "start" && (
                <p className="error" id="forecast-field-error" data-testid="forecast-field-error">
                  {wrong.text}
                </p>
              )}
              <label htmlFor="forecast-weeks">Visits</label>
              <select id="forecast-weeks" data-testid="forecast-weeks" value={weeks} onChange={(event) => setWeeks(event.target.value)}>
                {weekGroupsFor(plan.week_group).map((group) => (
                  <option key={group} value={group}>
                    {weekGroupLabel(group)}
                  </option>
                ))}
              </select>
              <dl className="facts" data-testid="forecast-preview">
                <dt>Next pickup now</dt>
                <dd>{dueText(before, today)}</dd>
                <dt>After the reset</dt>
                <dd data-testid="forecast-preview-after">{dueText(after, today)}</dd>
              </dl>
            </>
          ) : (
            <p>The forecast uses all pickups again.</p>
          )}
          <label htmlFor="forecast-reason">Reason</label>
          <input
            id="forecast-reason"
            data-testid="forecast-reason"
            value={reason}
            ref={reasonRef}
            autoFocus
            aria-invalid={wrong?.field === "reason" || long}
            aria-describedby={wrong?.field === "reason" || long ? "forecast-reason-error" : undefined}
            onChange={(event) => {
              setReason(event.target.value);
              setWrong(null);
            }}
          />
          {(wrong?.field === "reason" || long) && (
            <p className="error" id="forecast-reason-error" data-testid="forecast-field-error">
              {long ? reasonProblem(reason) : wrong?.text}
            </p>
          )}
          <div className="actions">
            <button data-testid="forecast-reset-save" disabled={saving}>
              {saving ? "Saving…" : open === "clear" ? "Clear the reset" : "Reset the forecast"}
            </button>
            <button type="button" onClick={close}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
